<template>
  <div class="f-h config-indics">
    <div class="f-h">
      <PanelSplitter class="f-h" :allow-hiding="[false, true]" :custom-width-perc="[60, 40]">
        <div class="library-content f-h">
          <h2 class="basic-sub-title h-v-centerer">
            {{ $t('message.INDICATORS_LIBRARY') }}
            <HelperButton :content="$t('message.LIBRARY_INDIC_HINT')" />
          </h2>
          <div class="v-centerer top-buttons">
            <button class="add-button blue" @click="expandCollapseAll(oneItemExpanded)">
              {{ oneItemExpanded ? $t('message.CONFIG_COLLAPSE_ALL') : $t('message.CONFIG_EXPAND_ALL') }}
            </button>
            <button class="add-button blue add-theme" @click="addTheme">
              <img src="/images/add-blue.png" alt="">
              {{ $t('message.ADD_THEME') }}
            </button>
          </div>
          <div
            v-for="theme in libraryThemes"
            :key="theme.id"
            class="library-theme"
          >
            <div class="library-theme__expander v-centerer" @click="theme.expanded = !theme.expanded">
              <Icon :w="12" class="library-theme__expander__expand-img">
                {{ wideArrowRightMedium }}
              </Icon>
              <EditableLabel :value="themeTitle(theme)" :disabled="true" />
              <Icon
                v-if="theme.id > 0"
                class="library-theme__expander__delete-img"
                @click.native.stop="deleteTheme(theme.id)"
              >
                {{ deleteMedium }}
              </Icon>
            </div>
            <TransitionExpand>
              <div v-if="theme.expanded" class="library-theme__expandable">
                <EditableLabel
                  v-if="theme.id !== 0"
                  v-model="theme.name"
                  class="theme-name"
                  :placeholder="$t('message.THEME_NAME_PH')"
                  @input="editTheme(theme)"
                />
                <LibraryIndicatorItem
                  v-for="indic in theme.indicators"
                  :key="indic.id"
                  :indicator="indic"
                  :all-indicators="libraryThemes"
                  :all-teams="allTeams"
                  @edit="editIndicator"
                  @merge="mergeIndicators"
                  @merge-click="mergeClicked(indic)"
                  @delete="deleteIndicator(indic)"
                />
                <button
                  v-if="theme.id !== 0"
                  class="add-button blue add-indic"
                  @click="addIndicator(theme)"
                >
                  <img src="@/assets/img/add-bold-blue.png" alt="">
                  {{ $t('message.ADD_INDICATOR') }}
                </button>
              </div>
            </TransitionExpand>
          </div>
        </div>
        <div class="f-h">
          <h2 class="basic-sub-title">
            {{ $t('message.CONFIG_INDIC_SCHEMES') }}
          </h2>
          <SchemesList ref="schemes" :selected-scheme="selectedScheme" @select="selectedScheme = $event" />
        </div>
      </PanelSplitter>
    </div>
    <EditableLabelRadio
      ref="merge"
      class="invisible-field"
      :value="0"
      :items="mergeIndicItems"
      label-key="name"
      ok-button
      :hint="$t('message.LIBRARY_INDIC_MERGE_HINT')"
      @input="mergeIndicators($event)"
    />
  </div>
</template>

<script>

import { cloneDeep } from 'lodash';
import toastr from 'toastr';
// Vuex
import { mapState } from 'vuex';
import { FETCH_LIBRARY_INDICATORS, FETCH_TEAMS } from '@/store/action-types';
import { GET_LIBRARY_INDICATORS, GET_TEAMS } from '@/store/mutation-types';
import { FETCH_SCHEMES } from '@/store/modules/config/action-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import HelperButton from '@/components/helper-button.vue';
import Icon from '@/components/icon.vue';
import LibraryIndicatorItem from '@/components/library-indicator-item.vue';
import PanelSplitter from '@/components/panel-splitter.vue';
import SchemesList from '@/components/schemes-list.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Mixins
import mixinConfigPages from '@/mixins/config-pages.vue';
// Utils
import { calert } from '@/utils/main';
import { deleteMedium, wideArrowRightMedium } from '@/assets/icons';

export default {

  name: 'ConfigIndicators',

  components: {
    EditableLabel,
    EditableLabelRadio,
    HelperButton,
    Icon,
    LibraryIndicatorItem,
    PanelSplitter,
    SchemesList,
    TransitionExpand,
  },

  mixins: [mixinConfigPages],

  data() {
    return {
      deleteMedium,
      wideArrowRightMedium,
      libraryThemes: [],
      // ID of an indicator to merge into another one
      toMergeId: 0,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.INDICATORS'),
    };
  },

  computed: {
    selectedScheme: {
      get() {
        return isNaN(this.$route.query.scheme) ? 0 : Number(this.$route.query.scheme);
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        if (newVal === 0) {
          delete query.scheme;
          this.refresh();
        } else {
          query.scheme = newVal;
        }
        this.$router.replace({ query });
      },
    },
    /** Boolean: Whether at last one item is expanded */
    oneItemExpanded() {
      return this.libraryThemes.some(theme => theme.expanded);
    },
    mergeIndicItems() {
      return this.libraryThemes.map(t => ({
        id: t.id,
        title: t.name,
        radioItems: t.indicators,
      }));
    },
    ...mapState({
      libThemesLoading: state => state[GET_LIBRARY_INDICATORS.loadingKey],
      allTeams: state => state[GET_TEAMS.stateKey] || [],
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
  },

  methods: {
    refresh() {
      if (!this.allTeamsLoading) {
        this.$store.dispatch(FETCH_TEAMS, { config: 1 });
      }
      if (!this.libThemesLoading) {
        this.$store.dispatch(FETCH_LIBRARY_INDICATORS, { config: 1 }).then(() => {
          this.updateLibIndicators(cloneDeep(this.$store.state[GET_LIBRARY_INDICATORS.stateKey] || []));
        });
      }
      this.$store.dispatch(`config/${FETCH_SCHEMES}`);
    },
    /** Expand or collapse all items */
    expandCollapseAll(collapse) {
      this.libraryThemes.forEach((theme) => {
        theme.expanded = !collapse;
      });
    },
    themeTitle(theme) {
      if (theme.id === 0) {
        return this.$t('message.LIBRARY_INDIC_UNCATEGORIZED');
      }
      return theme.name;
    },
    /** Create a new theme */
    addTheme() {
      this.App.post('/server/config/library/addTheme.php', {}, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
        }
      });
    },
    /** Creates a new indicator under a theme */
    addIndicator(theme) {
      this.App.post('/server/config/library/add.php', { theme: theme.id }, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
        }
      });
    },
    /** Edits theme name */
    editTheme(theme) {
      this.App.post('/server/config/library/editTheme.php', { id: theme.id, name: theme.name }, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
          toastr.success(this.$t('message.INDICATORS_SAVED'));
        }
      });
    },
    /** Edit the name of a library indicator */
    editIndicator(field, indicator) {
      const params = { id: indicator.id };
      params[field] = indicator[field];
      this.App.post('/server/config/library/edit.php', params, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
          toastr.success(this.$t('message.INDICATORS_SAVED'));
        }
      });
    },
    /** 'merge' clicked on an indicator item */
    mergeClicked(item) {
      this.toMergeId = item.id;
      this.$refs.merge.edit();
    },
    /** Merge two library indicators */
    mergeIndicators(mergeIntoId) {
      if (mergeIntoId === this.toMergeId) {
        calert(this.$t('message.LIBRARY_INDIC_MERGE_SAME'), [{ title: this.$t('message.CANCEL') }]);
        return;
      }
      this.App.post('/server/config/library/merge.php', { toMerge: this.toMergeId, mergeInto: mergeIntoId }, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
          toastr.success(this.$t('message.INDICATORS_SAVED'));
        }
      });
    },
    /** Deletes a theme */
    deleteTheme(themeId) {
      calert(this.$t('message.LIBRARY_INDIC_DELETE_THEME_CONFIRM'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.confirmDeleteTheme(themeId);
          },
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    confirmDeleteTheme(themeId) {
      this.App.post('/server/config/library/deleteTheme.php', { id: themeId }, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
        }
      });
    },
    /** Deletes an indicator */
    deleteIndicator(indicator) {
      calert(this.$t('message.LIBRARY_INDIC_DELETE_CONFIRM'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.confirmDeleteIndicator(indicator);
          },
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Deletes an indicator */
    confirmDeleteIndicator(indicator) {
      this.App.post('/server/config/library/delete.php', { id: indicator.id }, (d) => {
        if (d instanceof Array) {
          this.updateLibIndicators(d);
          this.$refs.schemes.fetchSchemes();
        }
      });
    },
    /** Refresh indicators with new data */
    updateLibIndicators(data) {
      const expanded = this.libraryThemes.filter(t => t.expanded).map(t => t.id);
      data.forEach((t) => { t.expanded = expanded.includes(t.id); });
      this.libraryThemes = data;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.config-indics .panel-splitter__panel {
  overflow-x: hidden;
}
.library-content {

  > .field-hint {
    padding: 0px 20px 10px 20px;
  }
  > .top-buttons {
    justify-content: space-between;

    .add-button.blue {
      padding: 5px 10px;
    }
  }


  .library-theme {
    margin-top: 0.1em;

    &__expander {
      height: 35px;
      background: #c8cee5;
      padding-left: 0.6em;
      cursor: pointer;

      &__expand-img {
        margin-right: 1em;
      }

      &__delete-img.icon {
        position: absolute;
        right: 0px;
        height: 1.5em;
        width: 1.5em;
        transform: initial;
        padding: 0.5em;
        margin-right: 0.4em;

        &:hover {
          background: $color-hoverbtn-red;
        }
      }
    }
    &__expandable {
      padding: 0px;
      margin-left: 23px;
      border-left: 5px solid #8891b2;

      .add-indic {
        padding-bottom: 5px;
      }

      .theme-name.editable-label {
        padding: 5px;

        .editable-label-text, .textarea {
          box-sizing: border-box;
          border: 2px gray dashed;
          background: #fff;
          padding: 1px 4px;
          margin-top: 0px;
          min-height: 1.8em;
          max-height: 1.8em;
        }
      }
    }
    &:last-child {
      margin-bottom: 4em;
    }
  }
}
</style>
